import React from "react";
import { Table, CombinedTable } from "@/types/table";
import { TablePosition, TableShape } from "@/hooks/useFloorPlanPositions";
import { cn } from "@/lib/utils";
import { GripVertical } from "lucide-react";

interface EditableFloorPlanTableProps {
  position: TablePosition;
  table: Table | CombinedTable;
  cellSize: number;
  isSelected: boolean;
  dragTransform?: { dx: number; dy: number } | null;
  resizeTransform?: { dw: number; dh: number } | null;
  zoneColor?: string;
  isHighlighted?: boolean;
  /** Si true, la table chevauche une autre — encadrée en rouge (warning visuel non bloquant) */
  hasOverlap?: boolean;
}

const getCurrentShape = (width: number, height: number, savedShape?: TableShape): TableShape => {
  if (savedShape === 'round') return 'round';
  if (width === height) return 'square';
  if (width > height) return 'horizontal';
  return 'vertical';
};

export const EditableFloorPlanTable = React.memo(({
  position,
  table,
  cellSize,
  isSelected,
  dragTransform,
  resizeTransform,
  zoneColor,
  isHighlighted = false,
  hasOverlap = false,
}: EditableFloorPlanTableProps) => {
  const currentShape = getCurrentShape(position.width, position.height, position.shape);
  const isRound = currentShape === 'round';
  const rotationDeg = position.rotation || 0;
  const isDragging = !!dragTransform;
  const isResizing = !!resizeTransform;

  const visualWidth = position.width * cellSize - 4 + (isResizing ? resizeTransform!.dw : 0);
  const visualHeight = position.height * cellSize - 4 + (isResizing ? resizeTransform!.dh : 0);

  const style: React.CSSProperties = {
    left: `${position.x * cellSize}px`,
    top: `${position.y * cellSize}px`,
    width: `${Math.max(20, visualWidth)}px`,
    height: `${Math.max(20, visualHeight)}px`,
    transform: isDragging
      ? `translate(${dragTransform!.dx}px, ${dragTransform!.dy}px) scale(1.05) rotate(${rotationDeg}deg)`
      : rotationDeg ? `rotate(${rotationDeg}deg)` : undefined,
    willChange: isDragging ? 'transform' : undefined,
    transition: isDragging ? 'none' : undefined,
    zIndex: isDragging ? 100 : isSelected ? 50 : 20,
  };

  const capacity = 'capacity' in table ? table.capacity : table.totalCapacity;

  return (
    <div
      className="absolute"
      style={style}
      data-table-id={position.tableId}
    >
      <div
        className={cn(
          "w-full h-full border-2 border-dashed transition-colors flex flex-col items-center justify-center text-center cursor-move select-none",
          (() => { const d = Math.min(position.width, position.height) * cellSize; return d < 35 ? "p-0.5" : d < 50 ? "p-1" : "p-2"; })(),
          isRound ? "rounded-full" : "rounded-lg",
          "bg-muted/60 border-muted-foreground/40 text-foreground",
          isSelected && "ring-2 ring-primary border-primary/70 shadow-lg bg-primary/5",
          isDragging && "opacity-80 shadow-xl",
          isHighlighted && "ring-4 ring-yellow-500 animate-pulse shadow-lg shadow-yellow-500/40",
          hasOverlap && "ring-2 ring-destructive border-destructive bg-destructive/10",
        )}
        style={{
          borderLeftColor: zoneColor || undefined,
          borderLeftWidth: zoneColor ? 4 : undefined,
        }}
      >
        {/* Grip icon */}
        {Math.min(position.width, position.height) * cellSize >= 40 && (
          <GripVertical className="absolute top-0.5 right-0.5 w-3 h-3 opacity-30" />
        )}

        {/* Zone color dot */}
        {zoneColor && Math.min(position.width, position.height) * cellSize >= 40 && (
          <span
            className="absolute top-1 left-1 w-2 h-2 rounded-full"
            style={{ backgroundColor: zoneColor }}
          />
        )}

        {/* Resize handle - only when selected */}
        {isSelected && !isDragging && (
          <div
            className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-primary border border-primary-foreground rounded-sm cursor-nwse-resize z-30 hover:scale-125 transition-transform flex items-center justify-center"
            data-resize-handle
          >
            <svg width="6" height="6" viewBox="0 0 6 6" className="text-primary-foreground">
              <line x1="5" y1="1" x2="1" y2="5" stroke="currentColor" strokeWidth="1" />
              <line x1="5" y1="3" x2="3" y2="5" stroke="currentColor" strokeWidth="1" />
            </svg>
          </div>
        )}

        {/* Table name */}
        <div
          className={cn(
            "font-semibold w-full text-center leading-tight",
            Math.min(position.width, position.height) * cellSize >= 50 ? "truncate" : "overflow-visible whitespace-nowrap"
          )}
          style={{
            fontSize: `clamp(10px, ${Math.min(position.width, position.height) * cellSize * 0.3}px, 14px)`,
          }}
        >
          {table.name}
        </div>

        {/* Capacity - hidden when table is too small */}
        {Math.min(position.width, position.height) * cellSize >= 35 && (
          <div
            className="text-muted-foreground truncate w-full text-center"
            style={{
              fontSize: `clamp(7px, ${Math.min(position.width, position.height) * cellSize * 0.18}px, 11px)`,
            }}
          >
            {capacity}p
          </div>
        )}
      </div>
    </div>
  );
});

EditableFloorPlanTable.displayName = 'EditableFloorPlanTable';
